import React from "react";
import { Icon } from "./icons";

/**
 * Komponen UI kecil reusable untuk admin dashboard (Tailwind, tanpa library).
 */

export function Button({ children, variant = "primary", size = "md", className = "", ...props }) {
  const base =
    "inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-1";
  const variants = {
    primary: "bg-brand-600 text-white hover:bg-brand-700 focus:ring-brand-400",
    secondary: "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 focus:ring-slate-300",
    danger: "bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-400",
    success: "bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-400",
    ghost: "text-slate-600 hover:bg-slate-100 focus:ring-slate-300",
  };
  const sizes = {
    sm: "px-2.5 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-base",
  };
  return (
    <button className={`${base} ${variants[variant]} ${sizes[size]} ${className}`} {...props}>
      {children}
    </button>
  );
}

/** Tombol aksi baris "•••" (buka detail) — gaya konsisten di semua list view. */
export function DotsButton({ onClick, title = "Lihat detail", className = "" }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-brand-600 hover:bg-brand-50 transition ${className}`}
    >
      <Icon.Dots size={18} />
    </button>
  );
}

/**
 * Peta lokasi absensi. Menampilkan preview peta (Google Maps embed — tanpa
 * perlu API key) + alamat + koordinat. Seluruh area peta bisa DIKLIK untuk
 * membuka Google Maps di tab baru (cek posisi absen karyawan). Bila koordinat
 * GPS tidak tersedia, hanya alamat yang ditampilkan.
 */
export function MapLocation({ label, lat, lng, address }) {
  const nLat = lat == null ? NaN : Number(lat);
  const nLng = lng == null ? NaN : Number(lng);
  const hasCoord = !Number.isNaN(nLat) && !Number.isNaN(nLng);
  const q = hasCoord ? `${nLat},${nLng}` : null;
  const mapsUrl = hasCoord ? `https://www.google.com/maps/search/?api=1&query=${q}` : null;
  const embedUrl = hasCoord ? `https://maps.google.com/maps?q=${q}&z=16&output=embed` : null;

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
      <div className="px-3 py-2 flex items-center justify-between">
        <span className="text-xs text-slate-500">{label}</span>
        {mapsUrl && (
          <a href={mapsUrl} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-brand-600">
            Buka Maps ↗
          </a>
        )}
      </div>
      {hasCoord ? (
        <a
          href={mapsUrl}
          target="_blank"
          rel="noreferrer"
          className="relative block group"
          title="Klik untuk buka lokasi di Google Maps"
        >
          {/* iframe = preview peta; pointer-events-none supaya klik diteruskan ke <a> */}
          <iframe
            title={`Peta ${label}`}
            src={embedUrl}
            className="w-full h-36 border-0 pointer-events-none"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <span className="absolute inset-0" aria-hidden="true" />
          <span className="absolute bottom-1.5 right-1.5 bg-white/90 group-hover:bg-white text-[10px] px-1.5 py-0.5 rounded shadow-sm text-slate-600">
            📍 {q}
          </span>
        </a>
      ) : (
        <div className="h-20 flex items-center justify-center text-xs text-slate-400">
          Tidak ada koordinat GPS
        </div>
      )}
      <div className="px-3 py-2 text-xs text-slate-500 break-words">
        {address || "-"}
      </div>
    </div>
  );
}

/** Avatar inisial nama (bulat, hijau brand). */
export function Avatar({ name, size = 36, className = "" }) {
  const text = (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return (
    <div
      className={`rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-bold shrink-0 ${className}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.33) }}
    >
      {text}
    </div>
  );
}

/**
 * Kartu statistik ringkas (dashboard singkat): judul, angka besar + satuan,
 * pill tren vs periode sebelumnya, dan keterangan. `goodWhenUp=false` untuk
 * metrik yang lebih baik bila turun (telat, anomali) → naik = merah.
 */
export function StatCard({ title, value, unit, delta, deltaSuffix = "%", goodWhenUp = true, description, icon: IconC, loading = false }) {
  const hasDelta = delta != null && Number.isFinite(delta);
  const tone = !hasDelta || delta === 0 ? "neutral" : (delta > 0) === goodWhenUp ? "good" : "bad";
  const toneCls = {
    good: "bg-[#cffae5] text-[#2f7a62]",
    bad: "bg-[#fce3e5] text-[#a2445a]",
    neutral: "bg-slate-100 text-slate-500",
  }[tone];
  return (
    <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.05)] px-4 py-3.5 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] font-medium text-slate-600 truncate">{title}</span>
        {IconC && (
          <span className="h-7 w-7 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
            <IconC size={15} />
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-1.5 mt-1.5">
        {loading ? (
          <span className="h-8 w-20 rounded-lg bg-slate-100 animate-pulse" />
        ) : (
          <>
            <span className="text-[28px] leading-none font-semibold text-slate-800 tracking-tight">{value ?? "-"}</span>
            {unit && <span className="text-sm text-slate-400">{unit}</span>}
            {hasDelta && (
              <span className={`ml-1 self-center px-1.5 py-0.5 rounded-md text-[11px] font-semibold ${toneCls}`}>
                {delta > 0 ? "+" : ""}
                {delta}
                {deltaSuffix}
              </span>
            )}
          </>
        )}
      </div>
      {description && <p className="text-[11px] text-slate-400 mt-2 leading-snug">{description}</p>}
    </div>
  );
}

export function Card({ children, className = "" }) {
  return (
    <div className={`bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.06)] ${className}`}>{children}</div>
  );
}

// Palet badge dari design manual (hex tegas). green=hadir/normal, red=absent/anomali,
// blue=WFH, yellow=telat/half-day (harmonis), gray/purple netral.
const BADGE_COLORS = {
  green: "bg-[#cffae5] text-[#579681]",
  red: "bg-[#fce3e5] text-[#8f4657]",
  blue: "bg-[#dbebff] text-[#5a75b0]",
  yellow: "bg-[#fdeccb] text-[#a76a2e]",
  gray: "bg-[#eaecee] text-[#697586]",
  purple: "bg-[#ece4fb] text-[#6d54a8]",
};

export function Badge({ children, color = "gray", className = "" }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide ${BADGE_COLORS[color]} ${className}`}>
      {children}
    </span>
  );
}

export function Input({ label, className = "", ...props }) {
  return (
    <label className="block">
      {label && <span className="block text-sm font-medium text-slate-600 mb-1">{label}</span>}
      <input
        className={`w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 ${className}`}
        {...props}
      />
    </label>
  );
}

export function Select({ label, children, className = "", ...props }) {
  return (
    <label className="block">
      {label && <span className="block text-sm font-medium text-slate-600 mb-1">{label}</span>}
      <select
        className={`w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 ${className}`}
        {...props}
      >
        {children}
      </select>
    </label>
  );
}

export function Textarea({ label, className = "", ...props }) {
  return (
    <label className="block">
      {label && <span className="block text-sm font-medium text-slate-600 mb-1">{label}</span>}
      <textarea
        className={`w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 ${className}`}
        {...props}
      />
    </label>
  );
}

export function Modal({ open, onClose, title, subtitle, children, footer, wide = false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4 animate-fade-in" onClick={onClose}>
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[92vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-3.5 border-b border-slate-100 sticky top-0 bg-white z-10 rounded-t-2xl">
          <div>
            <h3 className="font-bold text-slate-800 leading-tight">{title}</h3>
            {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="h-8 w-8 -mr-1 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition">
            <Icon.Close size={18} />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50/70 rounded-b-2xl sticky bottom-0">{footer}</div>
        )}
      </div>
    </div>
  );
}

export function Table({ columns, rows, empty = "Tidak ada data", keyField = "id", onRowClick }) {
  return (
    <div className="overflow-auto h-full">
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-10">
          <tr className="text-left text-slate-700 bg-white border-b border-slate-200">
            {columns.map((c) => (
              <th key={c.key} className={`px-4 py-3 text-[13px] font-bold whitespace-nowrap ${c.className || ""}`}>{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-10 text-center text-slate-400">{empty}</td>
            </tr>
          ) : (
            rows.map((row, idx) => (
              <tr
                key={row[keyField] ?? idx}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`border-b border-slate-100 last:border-0 hover:bg-brand-50/40 transition-colors ${onRowClick ? "cursor-pointer" : ""}`}
              >
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-3 ${c.cellClassName || ""}`}>
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export function Spinner({ className = "" }) {
  return <div className={`inline-block h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600 ${className}`} />;
}

export function Loading({ label = "Memuat…" }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
      <Spinner />
      <span>{label}</span>
    </div>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-4 flex-wrap bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.06)] px-5 py-4">
      <div>
        <h1 className="text-xl font-bold text-slate-800">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Pagination({ page, pageSize, total, onPage }) {
  const maxPage = Math.max(0, Math.ceil((total || 0) / pageSize) - 1);
  if ((total || 0) <= pageSize) return null;
  return (
    <div className="flex items-center justify-between mt-3 text-sm text-slate-500">
      <span>Total: {total}</span>
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" disabled={page <= 0} onClick={() => onPage(page - 1)}>← Sebelumnya</Button>
        <span className="px-2 py-1.5">Hal {page + 1} / {maxPage + 1}</span>
        <Button variant="secondary" size="sm" disabled={page >= maxPage} onClick={() => onPage(page + 1)}>Berikutnya →</Button>
      </div>
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="text-3xl mb-2">⚠️</div>
      <p className="text-slate-600 mb-3">{message || "Gagal memuat data"}</p>
      {onRetry && <Button variant="secondary" size="sm" onClick={onRetry}>Coba lagi</Button>}
    </div>
  );
}
